import { createSelector } from '@ngrx/store';
import {
  hasRemovePromptInput,
  isComplianceConfig,
} from '../../assistants/assistant-builder/assistant-builder.helper';
import {
  MAX_FILE_UPLOAD_COUNT,
  MAX_FILE_UPLOAD_COUNT_FOR_COMPARISON_TRAIT_ASSISTANT,
  MAX_FILE_UPLOAD_COUNT_FOR_COMPLIANCE_TRAIT_ASSISTANT_REFERENCE,
  MAX_FILE_UPLOAD_COUNT_FOR_COMPLIANCE_TRAIT_ASSISTANT_REGULAR,
  MAX_FILE_UPLOAD_COUNT_FOR_EXTRACT_TRAIT_ASSISTANT,
  MIN_FILE_UPLOAD_COUNT_FOR_COMPARISON_TRAIT_ASSISTANT,
  MIN_FILE_UPLOAD_COUNT_FOR_COMPLIANCE_TRAIT_ASSISTANT,
  MIN_FILE_UPLOAD_COUNT_FOR_EXTRACT_TRAIT_ASSISTANT,
} from '../../chats.constants';
import { chatsFeature } from '../../store';
import {
  selectIsNewConversationWithAssistant,
  selectSelectedChatAssistant,
  selectSelectedChatDocuments,
} from './chats.selectors';

// ── Types ──────────────────────────────────────────────────────────────────────

export type ComplianceUploadPhase = 'reference' | 'regular' | 'complete';

export type ConversationCTADescriptor =
  | { show: false }
  | {
      show: true;
      mainTextKey: string;
      mainTextParams?: Record<string, unknown>;
      subTextKey?: string;
      subTextParams?: Record<string, unknown>;
      buttonTextKey: string;
      buttonTextParams?: Record<string, unknown>;
    };

/**
 * CTA descriptor plus upload disabled flag for the conversation CTA block.
 * Single selector so the page can build CTA state from one store read.
 */
export type ConversationCTADescriptorWithUploadDisabled =
  | { show: false }
  | {
      show: true;
      mainTextKey: string;
      mainTextParams?: Record<string, unknown>;
      subTextKey?: string;
      subTextParams?: Record<string, unknown>;
      buttonTextKey: string;
      buttonTextParams?: Record<string, unknown>;
      buttonIsDisabled: boolean;
    };

/** Icon config for the CTA block (selector-owned constants). */
const CTA_MAIN_ICON = {
  icon: 'document-circle-arrow-up',
  type: 'uiBasic',
} as const;
const CTA_BUTTON_ICON = { icon: 'attach', type: 'uiBasic' } as const;

/**
 * Full CTA "raw" state from the store: descriptor + button disabled + icon config.
 * The page only needs to translate i18n keys and attach the click handler.
 */
export type ConversationCTAState =
  | { shouldShow: false }
  | {
      shouldShow: true;
      mainIcon: typeof CTA_MAIN_ICON;
      mainTextKey: string;
      mainTextParams?: Record<string, unknown>;
      subTextKey?: string;
      subTextParams?: Record<string, unknown>;
      buttonIcon: typeof CTA_BUTTON_ICON;
      buttonTextKey: string;
      buttonTextParams?: Record<string, unknown>;
      buttonIsDisabled: boolean;
    };

export interface ConversationPromptTooltip {
  i18nKey: string | null;
  i18nParams: Record<string, unknown>;
  reason: string;
}

export interface ConversationPromptToastMessage {
  i18nKey: string;
  i18nParams: Record<string, unknown>;
  reason: string;
}

/** File-upload error flags derived from FileUploadV2Store.getFiles() for use in selectors. */
export interface FileUploadErrorFlags {
  hasUploadError: boolean;
  hasInvalidType: boolean;
  hasSizeExceeded: boolean;
}

/** Args for the combined file-upload error toast selector (document toasts + file-upload toasts). */
export interface FileUploadErrorToastSelectorArgs {
  localFilesUuids: string[];
  fileUploadErrors: FileUploadErrorFlags;
  supportedExtensions: string[];
  maxFileSizeMB: number;
}

/** Result of selectFileUploadDisabled: whether upload is disabled and why. */
export interface FileUploadDisabledResult {
  disabled: boolean;
  /** i18n key for the reason (when disabled or for the tooltip). */
  i18nKey: string | null;
  i18nParams: Record<string, unknown>;
  /** Debug / fallback reason. */
  reason: string;
}

/** Result of submit disabled check: whether disabled and why (for tooltip/a11y). */
export interface SubmitDisabledResult {
  disabled: boolean;
  reasonKey: string;
  reasonParams?: Record<string, unknown>;
  reason: string;
}

const TOOLTIP_UPLOAD_FIRST_MESSAGE_ONLY =
  'PROMPT_INPUT.TOOLTIP.UPLOAD_FILE_COMPARISON_FIRST_MESSAGE';
const TOOLTIP_UPLOAD_FILE_LIMIT_REACHED =
  'PROMPT_INPUT.TOOLTIP.ADD_SOURCE_FILE_LIMIT_REACHED';
const TOOLTIP_UPLOAD_FILE_ASSISTANT =
  'PROMPT_INPUT.TOOLTIP.UPLOAD_FILE_ASSISTANT';

// ── Internal selectors (moved from chats.selectors; only used by conversation-prompt) ──

/**
 * Whether the selected chat has reference documents (in messages, fromChat, and/or assistant).
 */
const selectSelectedChatHasReferenceDocuments = createSelector(
  selectSelectedChatDocuments,
  (selectedChatDocuments) => {
    const hasInAssistant = selectedChatDocuments.fromChatAssistant
      .filter((doc) => !doc.deletedAt)
      .some((doc) => doc.role === 'reference');
    const hasInChat = selectedChatDocuments.fromChat.some(
      (doc) => doc.role === 'reference',
    );
    const hasInMessages = selectedChatDocuments.fromUserChatMessages.some(
      (doc) => doc.role === 'reference',
    );
    return {
      hasSome: hasInAssistant || hasInChat || hasInMessages,
      hasInAssistant,
      hasInChat,
      hasInMessages,
    };
  },
);

/**
 * Whether the selected chat has regular documents (in messages, fromChat, and/or assistant).
 */
const selectSelectedChatHasRegularDocument = createSelector(
  selectSelectedChatDocuments,
  (selectedChatDocuments) => {
    const hasInAssistant = selectedChatDocuments.fromChatAssistant
      .filter((doc) => !doc.deletedAt)
      .some((doc) => doc.role === 'regular');
    const hasInChat = selectedChatDocuments.fromChat.some(
      (doc) => doc.role === 'regular',
    );
    const hasInMessages = selectedChatDocuments.fromUserChatMessages.some(
      (doc) => doc.role === 'regular',
    );
    return {
      hasSome: hasInAssistant || hasInChat || hasInMessages,
      hasInAssistant,
      hasInChat,
      hasInMessages,
    };
  },
);

/**
 * Whether the selected chat is currently in a "thinking" state (assistant is generating a response).
 */
const selectSelectedChatIsThinking = createSelector(
  chatsFeature.selectSelectedChat,
  (selectedChat) => selectedChat?.isThinking ?? false,
);

// ── Pure store selectors ───────────────────────────────────────────────────────

/**
 * Compliance has three phases across two upload flows:
 * 1. userWillUploadReferenceDocuments: true  → "reference" until ref in chat; then "regular" until conversation has regular doc; then "complete".
 * 2. userWillUploadReferenceDocuments: false → "reference" until assistant has refs; then "regular" until conversation has regular doc; then "complete".
 * Returns null when not a compliance assistant or config is invalid.
 */
export const selectComplianceUploadPhase = createSelector(
  selectSelectedChatAssistant,
  selectSelectedChatHasReferenceDocuments,
  selectSelectedChatHasRegularDocument,
  (assistant, ref, hasRegular): ComplianceUploadPhase | null => {
    if (
      assistant?.trait !== 'compliance' ||
      !isComplianceConfig(assistant.traitConfig)
    ) {
      return null;
    }
    const refsSatisfied = assistant.traitConfig.userWillUploadReferenceDocuments
      ? ref.hasInMessages
      : ref.hasInAssistant;
    if (!refsSatisfied) return 'reference';
    if (!hasRegular.hasInMessages) return 'regular';
    return 'complete';
  },
);

export const selectIsChatUnavailable = createSelector(
  selectSelectedChatAssistant,
  (assistant): boolean => (assistant ? assistant.status !== 'ready' : false),
);

export const selectDocumentLimits = createSelector(
  selectSelectedChatAssistant,
  selectComplianceUploadPhase,
  (assistant, compliancePhase): { min: number; max: number } => {
    const trait = assistant?.trait ?? null;
    switch (trait) {
      case 'comparison':
        return {
          min: MIN_FILE_UPLOAD_COUNT_FOR_COMPARISON_TRAIT_ASSISTANT,
          max: MAX_FILE_UPLOAD_COUNT_FOR_COMPARISON_TRAIT_ASSISTANT,
        };
      case 'extract':
        return {
          min: MIN_FILE_UPLOAD_COUNT_FOR_EXTRACT_TRAIT_ASSISTANT,
          max: MAX_FILE_UPLOAD_COUNT_FOR_EXTRACT_TRAIT_ASSISTANT,
        };
      case 'compliance': {
        let max: number;
        if (compliancePhase === null) {
          max = MAX_FILE_UPLOAD_COUNT;
        } else if (compliancePhase === 'reference') {
          max = MAX_FILE_UPLOAD_COUNT_FOR_COMPLIANCE_TRAIT_ASSISTANT_REFERENCE;
        } else {
          max = MAX_FILE_UPLOAD_COUNT_FOR_COMPLIANCE_TRAIT_ASSISTANT_REGULAR;
        }
        return {
          min: MIN_FILE_UPLOAD_COUNT_FOR_COMPLIANCE_TRAIT_ASSISTANT,
          max,
        };
      }
      default:
        return { min: 0, max: MAX_FILE_UPLOAD_COUNT };
    }
  },
);

export const selectCurrentUploadRole = createSelector(
  selectComplianceUploadPhase,
  (compliancePhase): 'reference' | 'regular' =>
    compliancePhase === 'reference' ? 'reference' : 'regular',
);

/** Phase-aware remaining upload slots (reference vs regular) including localFilesUuids and fromChat. */
export const selectRemainingUploadSlots = (localFilesUuids: string[]) =>
  createSelector(
    selectDocumentLimits,
    selectDocumentCounts(localFilesUuids),
    (limits, counts): number => Math.max(0, limits.max - counts.inCurrentPhase),
  );

/**
 * Status of pending documents only (fromChat: attached to chat but not yet in messages).
 * Does not consider fromUserChatMessages or fromChatAssistant.
 */
export const selectPendingChatDocumentsStatus = createSelector(
  selectSelectedChatDocuments,
  (documents): { hasProcessing: boolean; hasErrors: boolean } => ({
    hasProcessing: documents.fromChat.some((d) => d.status === 'processing'),
    hasErrors: documents.fromChat.some((d) => d.status === 'failed'),
  }),
);

export const selectIsPromptTextRequired = createSelector(
  selectSelectedChatAssistant,
  selectIsNewConversationWithAssistant,
  selectComplianceUploadPhase,
  selectSelectedChatHasRegularDocument,
  (
    assistant,
    isNewConversation,
    compliancePhase,
    hasRegularDocument,
  ): boolean => {
    const trait = assistant?.trait ?? null;
    return !(
      (trait === 'comparison' && isNewConversation) ||
      (trait === 'compliance' &&
        compliancePhase !== 'complete' &&
        (compliancePhase === 'reference' ||
          (compliancePhase === 'regular' && hasRegularDocument.hasSome)))
    );
  },
);

/**
 * Full CTA state for the conversation page: when to show, i18n keys/params,
 * icon config, and button disabled. Template translates keys and handles click.
 */
/* eslint-disable sonarjs/cognitive-complexity -- trait/compliance branching in one place */
export const selectConversationCtaState = (localFilesUuids: string[]) =>
  createSelector(
    selectSelectedChatAssistant,
    selectIsNewConversationWithAssistant,
    selectComplianceUploadPhase,
    selectDocumentLimits,
    selectSelectedChatIsThinking,
    selectSelectedChatDocuments,
    selectFileUploadDisabled(localFilesUuids),
    chatsFeature.selectIsLastMessageAnimating,
    (
      assistant,
      isNewConversation,
      compliancePhase,
      limits,
      isThinking,
      documents,
      fileUploadDisabled,
      isLastMessageAnimating,
    ): ConversationCTAState => {
      const trait = assistant?.trait ?? null;
      const traitConfig = assistant?.traitConfig ?? null;
      const traitConfigHasRemovePromptInput = !!(
        traitConfig && hasRemovePromptInput(traitConfig)
      );
      const documentsHaveProcessing = documents.fromChat.some(
        (d) => d.status === 'processing',
      );
      // Hide CTA when last message is animating.
      // Hide CTA when documents are processing only if: removePromptInput is true (e.g. comparison),
      // or we're in compliance reference/regular phase (prompt is hidden there too).
      const hideCtaWhileProcessing =
        traitConfigHasRemovePromptInput ||
        (trait === 'compliance' &&
          (compliancePhase === 'reference' || compliancePhase === 'regular'));
      const showCta =
        !isLastMessageAnimating &&
        (!documentsHaveProcessing || !hideCtaWhileProcessing);

      const baseCtaState = {
        shouldShow: true as const,
        mainIcon: CTA_MAIN_ICON,
        buttonIcon: CTA_BUTTON_ICON,
        buttonIsDisabled: fileUploadDisabled.disabled,
      };

      if (
        trait === 'compliance' &&
        traitConfig &&
        isComplianceConfig(traitConfig)
      ) {
        const labels = traitConfig.labelForExpectedDocuments;
        if (compliancePhase === 'reference') {
          if (!showCta) return { shouldShow: false };
          return {
            ...baseCtaState,
            mainTextKey: 'ASSISTANTS.TRAITS.COMPLIANCE.CTA_TEXT',
            buttonTextKey:
              'ASSISTANTS.TRAITS.COMPLIANCE.CTA_BUTTON_ADD_FILES_TEXT',
            mainTextParams: { referenceLabel: labels.referenceLabel },
            subTextKey: 'ASSISTANTS.TRAITS.COMPLIANCE.CTA_SUBTEXT',
            subTextParams: { maxDocuments: limits.max },
            buttonTextParams: { maxDocuments: limits.max },
          };
        }
        if (
          compliancePhase === 'regular' &&
          !isThinking &&
          documents.fromChat.length === 0
        ) {
          if (!showCta) return { shouldShow: false };
          return {
            ...baseCtaState,
            mainTextKey: 'ASSISTANTS.TRAITS.COMPLIANCE.CTA_TARGET_DOC_TEXT',
            buttonTextKey:
              'ASSISTANTS.TRAITS.COMPLIANCE.CTA_BUTTON_ADD_FILES_TEXT',
            mainTextParams: { regularLabel: labels.regularLabel },
            subTextKey: 'ASSISTANTS.TRAITS.COMPLIANCE.CTA_TARGET_DOC_SUBTEXT',
            buttonTextParams: { maxDocuments: limits.max },
          };
        }
        return { shouldShow: false };
      }

      if (isNewConversation) {
        if (trait === 'comparison') {
          if (!showCta) return { shouldShow: false };
          return {
            ...baseCtaState,
            mainTextKey: 'ASSISTANTS.TRAITS.COMPARISON.CTA_TEXT',
            buttonTextKey:
              'ASSISTANTS.TRAITS.COMPARISON.CTA_BUTTON_ADD_FILES_TEXT',
            subTextKey: 'ASSISTANTS.TRAITS.COMPARISON.CTA_SUBTEXT',
            subTextParams: { maxDocuments: limits.max },
            buttonTextParams: { maxDocuments: limits.max },
          };
        }
        if (trait === 'extract') {
          if (!showCta) return { shouldShow: false };
          return {
            ...baseCtaState,
            mainTextKey: 'ASSISTANTS.TRAITS.EXTRACT.CTA_TEXT',
            buttonTextKey:
              'ASSISTANTS.TRAITS.EXTRACT.CTA_BUTTON_ADD_FILES_TEXT',
            buttonTextParams: { maxDocuments: limits.max },
          };
        }
      }

      return { shouldShow: false };
    },
  );
/* eslint-enable sonarjs/cognitive-complexity */

export const selectPromptOverlayTooltip = createSelector(
  selectIsChatUnavailable,
  selectPendingChatDocumentsStatus,
  (
    isChatUnavailable,
    pendingChatDocumentsStatus,
  ): ConversationPromptTooltip => {
    if (isChatUnavailable) {
      return {
        i18nKey: 'PROMPT_INPUT.TOOLTIP.CHAT_UNAVAILABLE',
        i18nParams: {},
        reason: 'Chat is unavailable — assistant is not in ready status',
      };
    }
    if (pendingChatDocumentsStatus.hasProcessing) {
      return {
        i18nKey: 'PROMPT_INPUT.TOOLTIP.ADD_SOURCE_PROCESSING_FILE',
        i18nParams: {},
        reason: 'Documents are still being processed by the server',
      };
    }
    return {
      i18nKey: null,
      i18nParams: {},
      reason:
        'No store-level overlay — component may still show local conditions',
    };
  },
);

// ── Factory selectors (depend on localFilesUuids) ──────────────────────────────

/**
 * Calculates document counts to determine upload limits and slot availability.
 * * Logic behavior:
 * - For Compliance traits: It tracks two separate pools (Reference vs. Regular).
 * 'inCurrentPhase' toggles based on whether the user is currently uploading
 * reference or regular documents.
 * * - For non-Compliance traits: The 'compliancePhase' is null. It defaults to
 * treating all documents as a single pool ('regular'), and 'inCurrentPhase'
 * mirrors that total.
 * * @param localFilesUuids - List of UUIDs for files currently being uploaded in the UI.
 * @returns { inCurrentPhase: number, regular: number }
 */
export const selectDocumentCounts = (localFilesUuids: string[]) =>
  createSelector(
    selectSelectedChatDocuments,
    selectComplianceUploadPhase,
    (
      documents,
      compliancePhase,
    ): { inCurrentPhase: number; regular: number } => {
      const uuids = (docs: { uuid: string }[]) => docs.map((d) => d.uuid);
      const { fromChat, fromUserChatMessages } = documents;

      const regularCount = new Set([
        ...localFilesUuids,
        ...uuids(fromChat.filter((d) => d.role === 'regular')),
        ...uuids(fromUserChatMessages.filter((d) => d.role === 'regular')),
      ]).size;

      if (compliancePhase === 'reference') {
        const referenceCount = new Set([
          ...localFilesUuids,
          ...uuids(fromChat.filter((d) => d.role === 'reference')),
          ...uuids(fromUserChatMessages.filter((d) => d.role === 'reference')),
        ]).size;
        return { inCurrentPhase: referenceCount, regular: regularCount };
      }

      return { inCurrentPhase: regularCount, regular: regularCount };
    },
  );

/* eslint-disable sonarjs/cognitive-complexity -- trait/disabled-reason branching */
export const selectFileUploadDisabled = (localFilesUuids: string[]) =>
  createSelector(
    selectSelectedChatAssistant,
    selectIsNewConversationWithAssistant,
    selectSelectedChatDocuments,
    selectSelectedChatHasRegularDocument,
    selectDocumentLimits,
    selectDocumentCounts(localFilesUuids),
    selectComplianceUploadPhase,
    (
      assistant,
      isNewConversation,
      documents,
      hasRegularDocument,
      limits,
      counts,
      compliancePhase,
    ): FileUploadDisabledResult => {
      const trait = assistant?.trait ?? null;
      const isChatUnavailable = assistant
        ? assistant.status !== 'ready'
        : false;

      const remainingUploadSlots = Math.max(
        0,
        limits.max - counts.inCurrentPhase,
      );
      const notDisabled: FileUploadDisabledResult = {
        disabled: false,
        i18nKey:
          counts.inCurrentPhase >= limits.max
            ? TOOLTIP_UPLOAD_FILE_LIMIT_REACHED
            : 'PROMPT_INPUT.TOOLTIP.MAX_FILES',
        i18nParams:
          counts.inCurrentPhase >= limits.max
            ? {}
            : { maxUploadCount: remainingUploadSlots },
        reason: 'Upload allowed',
      };

      if (isChatUnavailable) {
        return {
          disabled: true,
          i18nKey:
            trait === 'compliance' && compliancePhase !== 'complete'
              ? null
              : 'PROMPT_INPUT.TOOLTIP.CHAT_UNAVAILABLE',
          i18nParams: {},
          reason: 'Chat is unavailable — assistant is not in ready status',
        };
      }

      if (trait !== 'compliance') {
        if (counts.regular >= limits.max) {
          return {
            disabled: true,
            i18nKey: TOOLTIP_UPLOAD_FILE_LIMIT_REACHED,
            i18nParams: {},
            reason: `Document limit reached (${counts.regular}/${limits.max})`,
          };
        }
        if (
          hasRemovePromptInput(assistant?.traitConfig) &&
          (documents.fromChat.length > 0 || localFilesUuids.length > 0)
        ) {
          return {
            disabled: true,
            i18nKey: TOOLTIP_UPLOAD_FIRST_MESSAGE_ONLY,
            i18nParams: {},
            reason:
              'removePromptInput: upload disabled when documents are present',
          };
        }
      }

      switch (trait) {
        case 'insight':
          return notDisabled;
        case 'comparison':
        case 'extract':
          return isNewConversation
            ? notDisabled
            : {
                disabled: true,
                i18nKey: TOOLTIP_UPLOAD_FIRST_MESSAGE_ONLY,
                i18nParams: {},
                reason: `${trait} trait — upload is restricted to the first message only`,
              };
        case 'compliance':
          return !isNewConversation && hasRegularDocument.hasSome
            ? {
                disabled: true,
                i18nKey:
                  compliancePhase === 'complete'
                    ? TOOLTIP_UPLOAD_FILE_LIMIT_REACHED
                    : null,
                i18nParams: {},
                reason:
                  'Compliance trait — upload restricted after regular doc is present',
              }
            : notDisabled;
        case 'creative':
          return {
            disabled: true,
            i18nKey: TOOLTIP_UPLOAD_FILE_ASSISTANT,
            i18nParams: {},
            reason:
              'Upload in chat is always disabled for assistants with trait creative',
          };
        default:
          return notDisabled;
      }
    },
  );
/* eslint-enable sonarjs/cognitive-complexity */

export const selectPromptInputHidden = createSelector(
  selectSelectedChatAssistant,
  selectComplianceUploadPhase,
  (assistant, compliancePhase): boolean => {
    const trait = assistant?.trait ?? null;

    if (hasRemovePromptInput(assistant?.traitConfig)) return true;

    return (
      trait === 'compliance' &&
      (compliancePhase === 'reference' || compliancePhase === 'regular')
    );
  },
);

export const selectSubmitButtonTooltip = (localFilesUuids: string[]) =>
  createSelector(
    selectDocumentCounts(localFilesUuids),
    selectDocumentLimits,
    (counts, limits): ConversationPromptTooltip => {
      if (counts.inCurrentPhase < limits.min) {
        return {
          i18nKey: 'PROMPT_INPUT.TOOLTIP.ADD_SOURCE_FILE_UNDER_MIN_FILES',
          i18nParams: { minFiles: limits.min },
          reason: `Need at least ${limits.min} documents, currently have ${counts.inCurrentPhase}`,
        };
      }
      return {
        i18nKey: null,
        i18nParams: {},
        reason: 'Document count meets the minimum requirement',
      };
    },
  );

/**
 * Combined toast messages for the file-upload error toast: document state (count/limits/processing)
 * plus file-upload store errors (upload failed, invalid type, size exceeded).
 * Pass file errors and i18n params from the component so the selector stays pure and testable.
 */
export const selectFileUploadErrorToastMessages = (
  args: FileUploadErrorToastSelectorArgs,
) =>
  createSelector(
    selectDocumentCounts(args.localFilesUuids),
    selectDocumentLimits,
    selectPendingChatDocumentsStatus,
    (
      counts,
      limits,
      pendingChatDocumentsStatus,
    ): ConversationPromptToastMessage[] => {
      const messages: ConversationPromptToastMessage[] = [];

      if (pendingChatDocumentsStatus.hasErrors) {
        messages.push({
          i18nKey: 'PROMPT_INPUT.ADD_SOURCE_PROCESSING_ERROR',
          i18nParams: {},
          reason: 'One or more documents failed server-side processing',
        });
      }
      if (counts.inCurrentPhase > 0 && counts.inCurrentPhase < limits.min) {
        messages.push({
          i18nKey: 'PROMPT_INPUT.TOOLTIP.MIN_FILES',
          i18nParams: { minUploadCount: limits.min },
          reason: `Document count (${counts.inCurrentPhase}) is below the minimum required (${limits.min})`,
        });
      }
      if (counts.inCurrentPhase > limits.max) {
        messages.push({
          i18nKey: 'PROMPT_INPUT.ADD_SOURCE_COUNT_ERROR',
          i18nParams: { maxUploadCount: limits.max },
          reason: `Document count (${counts.inCurrentPhase}) exceeds the maximum allowed (${limits.max})`,
        });
      }
      if (args.fileUploadErrors.hasUploadError) {
        messages.push({
          i18nKey: 'PROMPT_INPUT.ADD_SOURCE_UPLOAD_ERROR',
          i18nParams: {},
          reason: 'Error uploading file',
        });
      }
      if (args.fileUploadErrors.hasInvalidType) {
        messages.push({
          i18nKey: 'PROMPT_INPUT.ADD_SOURCE_TYPE_ERROR',
          i18nParams: { types: args.supportedExtensions.join(', ') },
          reason: 'Invalid file type',
        });
      }
      if (args.fileUploadErrors.hasSizeExceeded) {
        messages.push({
          i18nKey: 'PROMPT_INPUT.ADD_SOURCE_SIZE_ERROR',
          i18nParams: { size: args.maxFileSizeMB },
          reason: 'File size exceeded',
        });
      }
      return messages;
    },
  );
